import React from "react";
import { PageHeader, SectionCard } from "@/components/Primitives";
import TranslationPanel from "@/components/TranslationPanel";
import { FileText, ClipboardList, GraduationCap } from "lucide-react";

/**
 * PatientOutputsDemo — v1 stub demo of the bilingual translation loop.
 *
 * Three cards, one per V1 document_type.  Each wraps the SAME
 * <TranslationPanel /> component — proving the "one function, many surfaces"
 * architecture the spec at /app/backend/specs/translation_service_v1.md
 * calls out.
 *
 * This page is patient-facing content preview.  Clinician-only surfaces
 * (SOAP, prior-auth letters, admin, learning queue) MUST NOT include
 * <TranslationPanel /> per policy.
 */
const SAMPLES = [
  {
    icon: FileText,
    title: "Discharge Summary",
    docType: "discharge_summary",
    text: `You were admitted to the hospital on July 3, 2026, for chest pain and shortness of breath. Testing showed you had a mild heart attack. You had a cardiac catheterization on July 4, and a stent was placed in one of your heart arteries.

Please take atorvastatin 40mg by mouth once at bedtime, aspirin 81mg once a day, and metoprolol 25mg twice a day. Do not miss doses. If you feel chest pain, please take one nitroglycerin under the tongue and call your doctor.

Follow up with your cardiologist within 7 days. Call 911 if chest pain returns and does not go away within 5 minutes.`,
    testid: "demo-discharge",
  },
  {
    icon: ClipboardList,
    title: "Patient Printed Instructions",
    docType: "patient_instructions",
    text: `Take your metformin 500mg with breakfast and dinner. Check your blood sugar before meals and at bedtime. Please write down each reading and bring the list to your next visit.

Drink at least 8 glasses of water each day. Avoid sugary drinks. You should walk for 30 minutes, 5 days a week. If your blood sugar is below 70 or above 300, call your doctor right away.`,
    testid: "demo-instructions",
  },
  {
    icon: GraduationCap,
    title: "ARIA — Patient Education",
    docType: "patient_education",
    text: `A stent is a small metal tube that keeps your heart artery open so blood can flow to your heart muscle. The stent stays in your artery for the rest of your life. It does not need to be removed.

You should take clopidogrel 75mg with your aspirin every day for at least one year. This helps prevent a blood clot from forming inside the stent. Please do not stop these medicines without talking to your cardiologist first, even for a dental procedure.`,
    testid: "demo-education",
  },
];

export default function PatientOutputsDemo() {
  return (
    <>
      <PageHeader
        eyebrow="Bilingual Support · v1 STUB"
        title="Patient Outputs — Translation Preview"
        subtitle="Toggle each card to Spanish (LatAm) or Portuguese (Brasil). Provider is currently a demo stub — real AWS Translate swaps in when credentials are staged."
        testid="patient-outputs-demo-header"
      />
      <div className="space-y-4">
        {SAMPLES.map(s => {
          const Icon = s.icon;
          return (
            <SectionCard
              key={s.docType}
              title={s.title}
              subtitle={`doc_type: ${s.docType}`}
              testid={`${s.testid}-card`}
            >
              <div className="flex items-start gap-3">
                <Icon className="w-5 h-5 text-brand-primary mt-1 shrink-0" strokeWidth={1.6} />
                <div className="flex-1 min-w-0">
                  <TranslationPanel
                    content={s.text}
                    docType={s.docType}
                    testid={`${s.testid}-panel`}
                  />
                </div>
              </div>
            </SectionCard>
          );
        })}
      </div>
      <div className="mt-6 text-[11px] text-brand-muted leading-relaxed max-w-2xl">
        <b>Architecture note:</b> every card above calls the same <code>&lt;TranslationPanel /&gt;</code>{" "}
        component, which posts to <code>/api/translate</code>. All translation logic lives in{" "}
        <code>/app/backend/translation.py</code> per the v1.0 spec. No inline translation calls
        exist anywhere else in the codebase — replaceable and portable to ProsthoAI / ReimbursePro
        via the same module.
      </div>
    </>
  );
}
